import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import { AppButton } from '../design-system';
import {
  Inventory,
  Add,
  Search,
  CloudOff,
  ErrorOutline,
} from '@mui/icons-material';

type EmptyStateType = 'no-data' | 'no-results' | 'error' | 'offline' | 'custom';

interface EmptyStateProps {
  type?: EmptyStateType;
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'contained' | 'outlined' | 'text';
  };
  compact?: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  type = 'no-data',
  icon,
  title,
  description,
  action,
  compact = false,
}) => {
  const getDefaultIcon = (type: EmptyStateType) => {
    switch (type) {
      case 'no-results':
        return <Search sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />;
      case 'error':
        return <ErrorOutline sx={{ fontSize: 64, color: 'error.main', opacity: 0.7 }} />;
      case 'offline':
        return <CloudOff sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />;
      default: // no-data
        return <Inventory sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />;
    }
  };

  const displayIcon = icon || getDefaultIcon(type);

  const content = (
    <Stack 
      spacing={compact ? 2 : 3} 
      alignItems="center" 
      textAlign="center"
      sx={{ 
        py: compact ? 3 : 6,
        px: 2,
      }}
    >
      {displayIcon}
      
      <Stack spacing={1} alignItems="center">
        <Typography 
          variant={compact ? "subtitle1" : "h6"} 
          component="h3"
          color="text.primary"
          fontWeight={500}
        >
          {title}
        </Typography>
        
        {description && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            maxWidth={400}
          >
            {description}
          </Typography>
        )}
      </Stack>

      {action && (
        <AppButton
          variant={action.variant || 'contained'}
          onClick={action.onClick}
          startIcon={type === 'no-data' ? <Add /> : undefined}
          size={compact ? 'medium' : 'large'}
        >
          {action.label}
        </AppButton>
      )}
    </Stack>
  );

  if (compact) {
    return <Box>{content}</Box>;
  }

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        border: 'none',
        boxShadow: 'none',
        backgroundColor: 'background.default',
      }}
    >
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};

export default EmptyState;